import {
    AuthContext,
    ClaimSearchResult,
    GuarantorSearchResult,
    PatientSearchResult,
    PayerSearchResult,
    ProviderSearchResult,
    RecentSearchResult,
    SupportClaim,
    SupportPatient,
} from '@enter/core';
import {mdiClose, mdiCloseCircle, mdiMagnify} from '@mdi/js';
import _ from 'lodash';
import { observer } from 'mobx-react';
import * as React from 'react';
import { ClaimSearchMode, ClaimSearchPresenter, ISearchResult } from './claim-search-bar.presenter';
import * as S from './claim-search-bar.styles';

interface IClaimSearchBarState {
    open: boolean;
    searching: boolean;
    selectedItem?: string;
    recentSearches: string[];
    searchResult: ISearchResult | undefined;
    searchTerm: string;
    searchMode: ClaimSearchMode;
}

export interface IClaimSearchBarProps {
    onChange: (claim: SupportClaim) => void;
    placeholder?: string;
    fieldMode?: boolean;
    disableEscapeClose?: boolean;
    disableNavigation?: boolean;
    // When true, treats UUIDs as claim IDs and uses direct lookup instead of search API
    treatUUIDAsClaimId?: boolean;
}

const maxRecentSearches = 9;
const debouncePeriod = 800;
const searchModes: ClaimSearchMode[] = ['patient', 'guarantor', 'provider', 'payer'];

@observer
export class ClaimSearchBar extends React.Component<IClaimSearchBarProps, IClaimSearchBarState> {
    public static contextType = AuthContext;
    private static saveRecentSearches = (recentSearches: string[]): void => {
        window.localStorage.setItem('search_recent', JSON.stringify(recentSearches));
    };

    private static loadRecentSearches = (): string[] => {
        const recent = window.localStorage.getItem('search_recent');
        if (!_.isNil(recent)) {
            return JSON.parse(recent);
        }
        return [];
    };

    private readonly presenter: ClaimSearchPresenter = new ClaimSearchPresenter();
    private readonly debouncedSearch: _.DebouncedFunc<(mode: ClaimSearchMode, term: string) => Promise<void>>;
    private readonly treatUUIDAsClaimId: boolean;

    constructor(props: IClaimSearchBarProps) {
        super(props);
        this.treatUUIDAsClaimId = props.treatUUIDAsClaimId ?? false;
        this.state = {
            open: false,
            searching: false,
            selectedItem: undefined,
            recentSearches: ClaimSearchBar.loadRecentSearches(),
            searchResult: undefined,
            searchTerm: '',
            searchMode: 'patient',
        };
        this.debouncedSearch = _.debounce(this.doSearch, debouncePeriod);
    }

    public componentDidMount(): void {
        window.addEventListener('keydown', this.handleKeyDown);
    }

    public componentWillUnmount(): void {
        window.removeEventListener('keydown', this.handleKeyDown);
    }

    public render(): React.ReactNode {
        return (
            <S.WrappedSearchBar
                open={this.state.open}
                text={this.state.searchTerm}
                placeholder={this.props.placeholder || 'Search in your claims'}
                controls={this.renderControls()}
                recent={this.renderRecent()}
                noResults={this.renderNoResults()}
                results={this.renderResults()}
                onChange={this.handleChange}
                onFocus={this.handleFocus}
                loading={this.state.searching}
                autoWidth={!!this.props.fieldMode}
                disableEscapeClose={this.props.disableEscapeClose}
            />
        );
    }

    private readonly findClaim = (claimId: string): SupportClaim | undefined => {
        const { searchResult } = this.state;
        if (searchResult) {
            return _.find(searchResult.claims, { id: claimId });
        }
    };

    private readonly renderControls = (): React.ReactNode => {
        return <S.Controls key="modes">
            <S.Header>
                <S.HeaderText>Search Results</S.HeaderText>
                <S.Spacer />
                <S.CloseButton onClick={this.doClose}>
                    <S.CloseIcon path={mdiCloseCircle} size={1} />
                </S.CloseButton>
            </S.Header>
        </S.Controls>;
        // const buttons = _.map(searchModes, mode => (
        //     <S.ModeButton
        //         key={mode}
        //         size="small"
        //         variant="outlined"
        //         active={this.state.searchMode === mode}
        //         onClick={this.handleModeClick(mode)}>
        //         {mode}
        //     </S.ModeButton>
        // ));
        // return (
        //     <S.Controls key="modes">
        //         <S.ModeTitle>Search mode</S.ModeTitle>
        //         {buttons}
        //         <S.Spacer />
        //         <S.CloseButton onClick={this.doClose}>
        //             <S.CloseIcon path={mdiClose} size={1} />
        //         </S.CloseButton>
        //     </S.Controls>
        // );
    };
    private readonly renderRecent = (): React.ReactNode | undefined => {
        return (
            <RecentSearchResult
                onClick={this.handleRecentClick}
                onDelete={this.handleRecentDelete}
                selected={this.state.selectedItem}
                terms={this.state.recentSearches}
            />
        );
    };
    private readonly renderNoResults = (): React.ReactElement[] => {
        return [
            <S.NoResults key="no-results">
                <S.NoResultsIcon path={mdiMagnify} size={1} />
                <S.NoResultsText>Your search criteria did not return any matches.</S.NoResultsText>
            </S.NoResults>,
        ];
    };
    private readonly renderResults = (): React.ReactElement[] | undefined => {
        let results: React.ReactElement[] | undefined;
        const { searchMode, searchTerm } = this.state;
        if (searchTerm) {
            if (searchMode === 'patient') {
                results = this.renderPatientResults();
            }
        }
        return _.isEmpty(results) ? undefined : results;
    };
    private readonly renderClaimResult = (claim: SupportClaim): React.ReactElement => {
        return (
            <ClaimSearchResult
                key={claim.id}
                id={claim.id}
                externalId={claim.accountNumber}
                fromDos={claim.fromDos}
                throughDos={claim.throughDos}
                primaryPayerName={claim.primaryPayerName}
                claimResponsibilityType={claim.claimResponsibilityType}
                charge={_.get(claim, 'chargeAmount.amount', '0')}
                selected={claim.id === this.state.selectedItem}
                onClick={this.handleClaimClick(claim)}
                disableNavigation={this.props.disableNavigation}
            />
        );
    };

    private readonly renderClaimResults = (claimIds: string[]): React.ReactNode => {
        const foundClaims = _.compact(_.map(claimIds, claimId => this.findClaim(claimId)));
        const claimSearchResults = _.map(foundClaims, claim => this.renderClaimResult(claim));
        return (
            <>
                <S.ClaimsRow>
                    <S.ClaimsHeader />
                    <S.ClaimsHeader>Date</S.ClaimsHeader>
                    <S.ClaimsHeader>Id</S.ClaimsHeader>
                    <S.ClaimsHeader>Status</S.ClaimsHeader>
                    <S.ClaimsHeader>Payer</S.ClaimsHeader>
                    <S.ClaimsHeader>Charge</S.ClaimsHeader>
                </S.ClaimsRow>
                {claimSearchResults}
            </>
        );
    };

    // private readonly renderPatientResults = (): React.ReactElement[] => {
    //     const { searchResult } = this.state;
    //     const map = this.buildClaimsMap(searchResult);
    //     const results = _.map(map, (claimIds: string[], patientKey: string) => {
    //         if (searchResult) {
    //             try {
    //                 // Prevent names like JAMES CONSTANTINE JR.
    //                 const patient = _.find(
    //                     searchResult.patients,
    //                     (p: SupportPatient) => `${p.first}${p.last}`.replace('.', '') === patientKey,
    //                 );
    //                 if (patient) {
    //                     const id = (patient && patient.id) || '';
    //                     const first = (patient && patient.first) || '';
    //                     const last = (patient && patient.last) || '';
    //                     const suffix = (patient && patient.suffix) || '';
    //                     const sex = (patient && patient.sex) || '';
    //                     const dob = (patient && patient.dob) || new Date(0).toISOString();
    //                     return (
    //                         <PatientSearchResult
    //                             key={patientKey}
    //                             id={id}
    //                             first={first}
    //                             last={last}
    //                             suffix={suffix}
    //                             sex={sex}
    //                             dob={dob}>
    //                             {this.renderClaimResults(claimIds)}
    //                         </PatientSearchResult>
    //                     );
    //                 }
    //             } catch (err) {
    //                 // NB: protect against un-renderable patients
    //                 // tslint:disable-next-line: no-console
    //                 console.error(err);
    //                 return <></>;
    //             }
    //         }
    //     });
    //     return _.compact(results);
    // };

    private readonly renderPatientResults = (): React.ReactElement[] => {
        const { searchResult } = this.state;
        const map = this.buildClaimsMap(searchResult);
        const results = _.map(map, (claimIds: string[], patientKey: string) => {
            if (searchResult) {
                try {
                    const claim = this.findClaim(claimIds[0]);
                    const id = (claim?.patientId) || '';
                    const first = (claim?.patientFirst) || '';
                    const last = (claim?.patientLast) || '';
                    const suffix = (claim?.patientSuffix) || '';
                    const externalId = (claim?.patientExternalId) || '';
                    const sex = '';
                    const dob = claim?.['patientDob'] || new Date(0).toISOString();
                    const nickname = (claim?.patientNickname) || '';
                    return (
                        <PatientSearchResult
                            key={patientKey}
                            id={id}
                            externalId={externalId}
                            first={first}
                            last={last}
                            suffix={suffix}
                            sex={sex}
                            nickname={nickname}
                            dob={dob}
                            disableNavigation={this.props.disableNavigation}>
                            {this.renderClaimResults(claimIds)}
                        </PatientSearchResult>
                    );
                } catch (err) {
                    // NB: protect against un-renderable patients
                    // tslint:disable-next-line: no-console
                    console.error(err);
                    return <></>;
                }
            }
        });
        return _.compact(results);
    };

    private readonly handleModeClick = (mode: ClaimSearchMode): (() => void) => () => {
        if (mode) {
            this.setState(
                {
                    searchMode: mode,
                },
                () => this.beginSearch(),
            );
        }
    };
    private readonly handleClaimClick = (claim: SupportClaim): (() => void) => {
        return (): void => {
            this.activateClaim(claim);
        };
    };
    private readonly handleRecentClick = (term: string): void => {
        this.activateRecent(term);
    };
    private readonly handleRecentDelete = (term: string): void => {
        const recentSearches = _.without(this.state.recentSearches, term);
        this.setState({ recentSearches });
        ClaimSearchBar.saveRecentSearches(recentSearches);
    };
    private readonly handleKeyDown = (e: KeyboardEvent): void => {
        const isBodyTarget = e.target === document.body;
        const selectedItem = this.state.selectedItem as string;
        if (isBodyTarget) {
            // NB: only consider printable chars if events are handled by 'body'
            if (e.code === 'Slash') {
                if (!this.state.open) {
                    this.setState({ open: true });
                    e.preventDefault();
                    e.stopPropagation();
                }
            }
        } else if (e.code === 'Escape') {
            this.doClose();
        } else if (selectedItem && e.code === 'Enter') {
            this.activateItem(selectedItem);
        } else if (e.code === 'ArrowDown' || e.code === 'ArrowUp') {
            this.setState({
                selectedItem: this.selectNextItem(e.code === 'ArrowUp'),
            });
        } else if (e.shiftKey && (e.code === 'ArrowLeft' || e.code === 'ArrowRight')) {
            const i = _.indexOf(searchModes, this.state.searchMode);
            const delta = e.code === 'ArrowRight' ? 1 : -1;
            const mode = searchModes[(i + searchModes.length + delta) % searchModes.length];
            this.handleModeClick(mode)();
        } else if (e.code === 'Delete' && e.shiftKey) {
            const recentSearches = _.without(this.state.recentSearches, selectedItem);
            this.setState({ recentSearches });
            ClaimSearchBar.saveRecentSearches(recentSearches);
        }
    };
    private readonly handleChange = async (searchTerm: string | undefined): Promise<void> => {
        if (!!searchTerm) {
            this.beginSearch(searchTerm);
        } else {
            this.setState({ searchTerm: '' });
        }
    };
    private readonly handleFocus = (): void => {
        if (!this.state.open) {
            this.setState({
                open: true,
            });
        }
    };
    private readonly beginSearch = (searchTerm: string = this.state.searchTerm): void => {
        const { searchMode } = this.state;
        if (!_.isEmpty(searchTerm)) {
            (this.context as any).cancelGroup('search');
            this.setState(
                {
                    searching: true,
                    searchTerm,
                },
                () => {
                    this.debouncedSearch.cancel();
                    this.debouncedSearch(searchMode, searchTerm);
                },
            );
        }
    };
    private readonly doClose = (): void => {
        (this.context as any).cancelGroup('search');
        this.setState({
            open: false,
            searching: false,
            searchTerm: '',
            searchResult: undefined,
            selectedItem: undefined,
        });
    };
    private readonly doSearch = async (mode: ClaimSearchMode, term: string): Promise<void> => {
        try {
            (this.context as any).setGroup('search');
            const searchResult = await this.presenter.searchForMode(mode, term, this.treatUUIDAsClaimId);
            this.setState({
                searchResult,
            });
            this.setState({
                searching: false,
            });
            const recentSearches = _.takeRight(_.uniq(_.concat(this.state.recentSearches, term)), maxRecentSearches);
            this.setState({
                recentSearches,
            });
            ClaimSearchBar.saveRecentSearches(recentSearches);
        } catch (err) {
            this.setState({
                searchResult: undefined,
            });
        }
    };
    private readonly buildClaimsMap = (searchResult?: ISearchResult): _.Dictionary<string[]> => {
        // const map = _.transform(
        //     _.get(searchResult, 'claims', []),
        //     (r, claim) => {
        //         // Prevent names like JAMES CONSTANTINE JR.
        //         const patientKey = `${claim.patientFirst}${claim.patientLast}`.replace('.', '');
        //         if (!r[patientKey]) {
        //             r[patientKey] = [];
        //         }
        //         r[patientKey].push(claim);
        //         const guarantorKey = claim.guarantorId;
        //         if (guarantorKey) {
        //             if (!r[guarantorKey]) {
        //                 r[guarantorKey] = [];
        //             }
        //             r[guarantorKey].push(claim);
        //         }
        //     },
        //     {},
        // );
        const map = _.transform(
            _.get(searchResult, 'claims', []),
            (r, claim) => {
                const patientKey = `${claim.patientFirst}${claim.patientLast}`.replace('.', '');
                if (!r[patientKey]) {
                    r[patientKey] = [];
                }
                r[patientKey].push(claim);
            },
            {},
        );
        return _.transform(
            map,
            (out: _.Dictionary<string[]>, claims: SupportClaim[], key: string) => {
                _.set(
                    out,
                    key,
                    _.orderBy(claims, ['fromDos'], ['desc']).map(claim => claim.id),
                );
            },
            {},
        );
    };
    private readonly selectNextItem = (up: boolean): string => {
        const { searchResult } = this.state;
        const size = _.reduce(
            _.values(searchResult),
            (r, arr) => {
                return arr ? r + arr.length : r;
            },
            0,
        );
        if (size > 0) {
            return this.selectNextResult(up);
        } else {
            return this.selectNextRecent(up);
        }
    };

    private readonly selectNextRecent = (up: boolean): string => {
        return this.selectNext(up, this.state.recentSearches);
    };

    private readonly selectNextResult = (up: boolean): string => {
        const patientClaimIds = _.values(this.buildClaimsMap(this.state.searchResult));
        return this.selectNext(up, _.flatten(patientClaimIds));
    };

    private readonly selectNext = (up: boolean, items: string[]): string => {
        const currIndex = items.indexOf(this.state.selectedItem as string);
        const nextIndex = up ? (currIndex - 1 + items.length) % items.length : (currIndex + 1) % items.length;
        return items[nextIndex];
    };

    private readonly activateItem = (item: string): void => {
        const claim = this.findClaim(item);
        if (claim) {
            this.activateClaim(claim);
        } else {
            this.activateRecent(item);
        }
    };

    private readonly activateClaim = (claim: SupportClaim): void => {
        if (claim.id) {
            this.doClose();
            this.props.onChange(claim);
        }
    };

    private readonly activateRecent = (term: string): void => {
        this.beginSearch(term);
    };
}
